import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { payrollApi, companyApi, projectApi } from '@/lib/api';
import { clampPageToTotal } from '@/lib/pagination';
import { DEPARTMENTS } from '@/lib/employeeOptions';
import { formatCurrency } from '@/lib/payrollCalc';
import { downloadBlobFile } from '@/lib/exportFile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarRange, Download, Loader2, Search } from 'lucide-react';

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const PAGE_SIZES = [24, 50, 100];

const currentFyStart = () => {
  const now = new Date();
  return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
};

const fyLabel = (year) => {
  const start = Number(year);
  if (!Number.isFinite(start)) return '—';
  return `FY ${start}-${String(start + 1).slice(-2)}`;
};

const fyYearOptions = () => {
  const current = currentFyStart();
  return [current - 1, current, current + 1];
};

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const toAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const employeeNameOf = (row) =>
  row?.name ||
  row?.employeeName ||
  `${row?.firstName ?? ''} ${row?.lastName ?? ''}`.trim() ||
  '—';

const employeeIdOf = (row) => row?.employeeId || row?.empId || row?.id || '';

const amountFrom = (row, keys) => {
  for (const key of keys) {
    if (row?.[key] != null && row?.[key] !== '') return toAmount(row[key]);
  }
  return 0;
};

const actualAmountFrom = (row, keys) => {
  const actual = row?.actualSalary;
  if (!actual || typeof actual !== 'object') return null;
  for (const key of keys) {
    if (actual?.[key] != null && actual?.[key] !== '') return toAmount(actual[key]);
  }
  return null;
};

const companyNameOf = (row) =>
  row?.companyDetails?.name || row?.companyName || row?.company || '—';

const projectDisplayName = (value, nameByKey) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    return value?.name || nameByKey?.get(String(value?.projectId ?? value?.code ?? '')) || value?.code || value?.projectId || '';
  }
  const key = String(value);
  return nameByKey?.get(key) || key;
};

const projectsLabelOf = (row, nameByKey) => {
  const projects = row?.projects;
  if (Array.isArray(projects) && projects.length) {
    return projects.map((p) => projectDisplayName(p, nameByKey)).filter(Boolean).join(', ') || '—';
  }
  if (typeof projects === 'string' && projects.trim()) return projectDisplayName(projects, nameByKey);
  if (row?.projectName) return String(row.projectName);
  if (row?.project) return projectDisplayName(row.project, nameByKey);
  return '—';
};

/** Current (pay-run) amount on top; actual salary below in blue. */
const AmountWithActual = ({ current, actual, emphasize = false }) => (
  <div className="text-right leading-tight">
    <p
      className={`font-mono text-xs ${
        emphasize ? 'font-semibold text-[#10b981]' : 'text-foreground'
      }`}
    >
      {formatCurrency(current)}
    </p>
    {actual != null && (
      <p className="font-mono text-xs text-[#0047AB] mt-0.5" title="Actual salary">
        {formatCurrency(actual)}
      </p>
    )}
  </div>
);

const SortableHead = ({
  label,
  sortKey,
  sortBy,
  sortOrder,
  onSort,
  align = 'left',
  className = '',
  sticky = false,
}) => {
  const active = sortBy === sortKey;
  const alignClass =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  const Icon = !active ? ArrowUpDown : sortOrder === 'asc' ? ArrowUp : ArrowDown;

  return (
    <TableHead
      className={`text-[10px] uppercase tracking-widest sticky top-0 z-20 bg-muted ${
        sticky ? 'left-0 z-30 min-w-[160px]' : ''
      } ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 w-full ${alignClass} hover:text-[#0047AB] transition-colors ${
          active ? 'text-[#0047AB] font-semibold' : ''
        }`}
        data-testid={`yearly-summary-sort-${sortKey}`}
        aria-label={`Sort by ${label}`}
      >
        <span>{label}</span>
        <Icon className={`h-3 w-3 shrink-0 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </TableHead>
  );
};

export default function YearlySummaryPage() {
  const [year, setYear] = useState(String(currentFyStart()));
  const [month, setMonth] = useState('ytd');
  const [company, setCompany] = useState('all');
  const [department, setDepartment] = useState('all');
  const [project, setProject] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [apiPeriodLabel, setApiPeriodLabel] = useState('');
  const [apiFinancialYear, setApiFinancialYear] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);

  const projectNameByKey = useMemo(() => {
    const map = new Map();
    (projects || []).forEach((p) => {
      const name = p?.name;
      if (!name) return;
      [p?.projectId, p?.id, p?.code, p?.name].forEach((key) => {
        if (key != null && key !== '') map.set(String(key), name);
      });
    });
    return map;
  }, [projects]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [comps, projs] = await Promise.all([
          companyApi.getAll().catch(() => []),
          projectApi.getAll().catch(() => []),
        ]);
        setCompanies(normalizeList(comps));
        setProjects(normalizeList(projs));
      } catch {
        setCompanies([]);
        setProjects([]);
      }
    };
    loadFilters();
  }, []);

  const buildParams = useCallback(
    ({ includePagination = true } = {}) => {
      const params = {
        year: Number(year),
        sortBy,
        sortOrder,
      };
      if (month !== 'ytd') params.month = Number(month);
      if (company !== 'all') params.company = company;
      if (department !== 'all') params.department = department;
      if (project !== 'all') params.project = project;
      if (debouncedSearch) params.search = debouncedSearch;
      if (includePagination) {
        params.page = page;
        params.limit = limit;
      }
      return params;
    },
    [year, month, company, department, project, debouncedSearch, sortBy, sortOrder, page, limit]
  );

  useEffect(() => {
    setPage(1);
  }, [year, month, company, department, project, debouncedSearch, sortBy, sortOrder, limit]);

  useEffect(() => {
    let cancelled = false;
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const response = await payrollApi.yearlySummary(buildParams());
        if (cancelled) return;
        setRows(Array.isArray(response?.items) ? response.items : []);
        setTotals(response?.totals && typeof response.totals === 'object' ? response.totals : {});
        setTotal(response?.total ?? 0);
        setApiPeriodLabel(response?.periodLabel || '');
        setApiFinancialYear(response?.financialYear || '');
        const nextPages = response?.pages ?? 1;
        setPages(nextPages);
        setPage((p) => clampPageToTotal(p, nextPages));
      } catch (err) {
        if (cancelled) return;
        setRows([]);
        setTotals({});
        setTotal(0);
        setPages(1);
        setApiPeriodLabel('');
        setApiFinancialYear('');
        toast.error(err?.message || 'Failed to load yearly summary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchSummary();
    return () => {
      cancelled = true;
    };
  }, [buildParams]);

  const periodLabel = useMemo(() => {
    if (apiPeriodLabel) {
      return apiFinancialYear ? `FY ${apiFinancialYear} · ${apiPeriodLabel}` : apiPeriodLabel;
    }
    if (month === 'ytd') return `${fyLabel(year)} · Apr → now`;
    const monthName = MONTHS.find((m) => m.value === month)?.label || month;
    return `${fyLabel(year)} · ${monthName}`;
  }, [apiPeriodLabel, apiFinancialYear, year, month]);

  const summaryCards = useMemo(() => {
    const source = totals || {};
    return [
      {
        label: 'Employees',
        value: source.employeeCount ?? total,
        color: '#0047AB',
      },
      {
        label: 'Total Gross',
        value: formatCurrency(amountFrom(source, ['totalGross', 'gross'])),
        color: '#10b981',
      },
      {
        label: 'Total Deductions',
        value: formatCurrency(amountFrom(source, ['totalDeductions'])),
        color: '#ef4444',
      },
      {
        label: 'Total Net',
        value: formatCurrency(amountFrom(source, ['totalNet', 'inHandSalary', 'totalInHand'])),
        color: '#6366f1',
      },
      {
        label: 'Total CTC',
        value: formatCurrency(amountFrom(source, ['totalCTC', 'totalCtc', 'ctc'])),
        color: '#f59e0b',
      },
    ];
  }, [totals, total]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortOrder('asc');
  };

  const handleClearFilters = () => {
    setYear(String(currentFyStart()));
    setMonth('ytd');
    setCompany('all');
    setDepartment('all');
    setProject('all');
    setSearch('');
    setDebouncedSearch('');
    setSortBy('name');
    setSortOrder('asc');
    setPage(1);
    setLimit(24);
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await payrollApi.exportYearlySummary(buildParams({ includePagination: false }));
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const matchedFileName = disposition.match(/filename="?([^"]+)"?/i)?.[1];
      const fileName =
        matchedFileName ||
        `payroll_yearly_summary_${year}${month !== 'ytd' ? `_${month}` : ''}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      downloadBlobFile(blob, fileName);
      toast.success('Yearly summary exported');
    } catch (err) {
      toast.error(err?.message || 'Failed to export yearly summary');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="yearly-summary-page" className="space-y-3 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-[Manrope]">Yearly Summary</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Payroll totals by financial year · {periodLabel}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Top value = pay-run total · <span className="text-[#0047AB]">blue below = actual salary</span>
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={exporting}
          data-testid="yearly-summary-export-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          {exporting ? 'Exporting...' : 'Export Excel'}
        </Button>
      </div>

      <Card className="border border-border shadow-sm">
        <CardContent className="p-3 sm:p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">
              Financial Year
            </label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-[140px] sm:w-[160px]" data-testid="yearly-summary-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {fyYearOptions().map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {fyLabel(y)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">
              Period
            </label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[150px] sm:w-[170px]" data-testid="yearly-summary-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ytd">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarRange className="w-3.5 h-3.5" /> Apr → now
                  </span>
                </SelectItem>
                {MONTHS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">
              Company
            </label>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger className="w-[150px] sm:w-[180px]" data-testid="yearly-summary-company-select">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies
                  .filter((c) => c?.name)
                  .map((c) => (
                    <SelectItem
                      key={c?.companyId ?? c?.id ?? c?.name}
                      value={c?.companyId ?? c?.id ?? c?.name}
                    >
                      {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">
              Department
            </label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger className="w-[150px] sm:w-[180px]" data-testid="yearly-summary-department-select">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {DEPARTMENTS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">
              Project
            </label>
            <Select value={project} onValueChange={setProject}>
              <SelectTrigger className="w-[150px] sm:w-[180px]" data-testid="yearly-summary-project-select">
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects
                  .filter((p) => p?.name)
                  .map((p) => (
                    <SelectItem
                      key={p?.projectId ?? p?.id ?? p?.name}
                      value={p?.projectId ?? p?.id ?? p?.name}
                    >
                      {p.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div className="min-w-[180px] flex-1">
            <label className="text-xs uppercase tracking-widest font-medium text-muted-foreground block mb-1.5">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="yearly-summary-search"
                placeholder="Name or employee ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>

          <Button variant="ghost" onClick={handleClearFilters} data-testid="yearly-summary-clear-btn">
            Clear
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
        {summaryCards.map((card) => (
          <Card key={card.label} className="border border-border shadow-sm">
            <CardContent className="p-3 sm:p-4 text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{card.label}</p>
              <p className="text-base sm:text-lg font-bold font-mono mt-1" style={{ color: card.color }}>
                {card.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border border-border shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-2 border-[#0047AB] border-t-transparent rounded-full" />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <CalendarRange className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No payroll summary found</p>
              <p className="text-xs mt-1">Try adjusting filters or financial year</p>
            </div>
          ) : (
            <Table containerClassName="max-h-[70vh] overflow-auto">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <SortableHead
                    label="Employee"
                    sortKey="name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    sticky
                    className="font-medium"
                  />
                  <TableHead className="text-[10px] uppercase tracking-widest sticky top-0 z-20 bg-muted">
                    Company
                  </TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest sticky top-0 z-20 bg-muted min-w-[120px]">
                    Projects
                  </TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-center sticky top-0 z-20 bg-muted">
                    Months
                  </TableHead>
                  <SortableHead
                    label="Basic"
                    sortKey="basic"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHead
                    label="Gross"
                    sortKey="gross"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHead
                    label="Eff. Gross"
                    sortKey="effectiveGross"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <TableHead className="text-[10px] uppercase tracking-widest text-right sticky top-0 z-20 bg-muted">
                    Deductions
                  </TableHead>
                  <SortableHead
                    label="In Hand"
                    sortKey="inHandSalary"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                  <SortableHead
                    label="CTC"
                    sortKey="ctc"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    align="right"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => {
                  const empId = employeeIdOf(row);
                  const name = employeeNameOf(row);
                  return (
                    <TableRow key={empId || `row-${index}`} data-testid={`yearly-summary-row-${empId || index}`}>
                      <TableCell className="sticky left-0 bg-background z-10 border-r">
                        <div>
                          {empId ? (
                            <Link
                              to={`/employees/${empId}`}
                              className="text-sm font-medium hover:text-[#0047AB] hover:underline"
                            >
                              {name}
                            </Link>
                          ) : (
                            <p className="text-sm font-medium">{name}</p>
                          )}
                          <p className="text-[10px] text-muted-foreground font-mono">
                            {empId || '—'}
                            {row?.department ? ` · ${row.department}` : ''}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{companyNameOf(row)}</TableCell>
                      <TableCell className="text-sm max-w-[180px]">
                        <span className="line-clamp-2" title={projectsLabelOf(row, projectNameByKey)}>
                          {projectsLabelOf(row, projectNameByKey)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center font-mono text-xs">
                        {row?.monthsCount ?? '—'}
                      </TableCell>
                      <TableCell>
                        <AmountWithActual
                          current={amountFrom(row, ['basic', 'basicSalary'])}
                          actual={actualAmountFrom(row, ['basic', 'basicSalary'])}
                        />
                      </TableCell>
                      <TableCell>
                        <AmountWithActual
                          current={amountFrom(row, ['gross'])}
                          actual={actualAmountFrom(row, ['gross'])}
                        />
                      </TableCell>
                      <TableCell>
                        <AmountWithActual
                          current={amountFrom(row, ['effectiveGross'])}
                          actual={null}
                        />
                      </TableCell>
                      <TableCell>
                        <AmountWithActual
                          current={amountFrom(row, ['totalDeductions'])}
                          actual={actualAmountFrom(row, ['totalDeductions'])}
                        />
                      </TableCell>
                      <TableCell>
                        <AmountWithActual
                          current={amountFrom(row, ['inHandSalary', 'netPay'])}
                          actual={actualAmountFrom(row, ['inHandSalary', 'netPay'])}
                          emphasize
                        />
                      </TableCell>
                      <TableCell>
                        <AmountWithActual
                          current={amountFrom(row, ['ctc'])}
                          actual={actualAmountFrom(row, ['ctc'])}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {total > 0
            ? `Showing ${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}`
            : 'No results'}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger className="w-[100px] h-9" data-testid="yearly-summary-limit-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} / page
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="yearly-summary-prev-page"
          >
            Previous
          </Button>
          <span className="text-sm tabular-nums px-1">
            {page} / {pages || 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pages || loading}
            onClick={() => setPage((p) => p + 1)}
            data-testid="yearly-summary-next-page"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
